import React, { useEffect, useState, SyntheticEvent } from "react";
import { Button, Modal, Box, Typography, Grid, TextField } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { nowOperationalDateTimeString } from "../../utils/operationalDateTime";
import Title from "../Title"; // Asumo que ya tienes el componente de título importado

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface MyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onButtonClick: () => void;
  id: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  width: {
    xs: '90%', // Ancho del 90% para pantallas pequeñas
    sm: '80%', // Ancho del 80% para pantallas medianas
    md: '70%', // Ancho del 70% para pantallas grandes
    lg: '60%', // Ancho del 60% para pantallas más grandes
  },
};

const MyModal: React.FC<MyModalProps> = ({
  isOpen,
  onClose,
  onButtonClick,
  id,
}) => {
  const [lendpay, setLendpay] = useState([]);
  const [location, setLocation] = useState("");
  const [value_discount, setValue_discount] = useState("");
  const [date, setDate] = useState("");
  const [value, setValue] = useState("");
  const [daily_pay_value, setDaily_pay_value] = useState("");
  const [pendiente, setPendiente] = useState(0);
  const navigate = useNavigate();

  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const coordinates = `${lat}, ${lng}`;
            resolve(coordinates);
          },
          error => {
            reject(error);
          }
        );
      } else {
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  const calculatePendiente = () => {
    const pagar = parseInt(daily_pay_value) - parseInt(value) - parseInt(value_discount);
    setPendiente(pagar);
  };

  useEffect(() => {
    calculatePendiente();
  }, [value, value_discount]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('user');
        setLendpay(data.data.id);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const lendpayData = await axios.get(`pays/${id}`);
        setLendpay(lendpayData.data);
        setValue("0");
        setValue_discount("0");
        setDaily_pay_value(lendpayData.data.lend.daily_pay_value);
      } catch (error) {
        console.error("Error fetching lendpay data:", error);
      }
    };

    const fetchCoordinates = async () => {
      try {
        const coords = await getCoordinates();
        setLocation(coords);
      } catch (error) {
        // handle error
      }
    };

    if (isOpen) {
      fetchData();
      fetchCoordinates();
    }
  }, [isOpen, id]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
  
    const currentDate = nowOperationalDateTimeString();
  
    const formData = {
      balance: pendiente,
      pay_date: currentDate,
      user_id: lendpay,
      state: true,
      value: parseInt(value),
      location,
      value_discount: parseInt(value_discount),
    };
  
    try {
      if (!navigator.onLine) {
        // Save data locally if offline
        localStorage.setItem(`pending-payment-${id}`, JSON.stringify(formData));
        // showAlert('Pago guardado localmente. Será enviado cuando el internet esté disponible.', 'info');
      } else {
        await axios.put(`/pays/${id}`, formData);
        // showAlert('Pago realizado exitosamente!', 'success');
        navigate(`/prestamos/abonos/pagos/invoice/${id}`);
        onButtonClick();
      }
    } catch (error) {
      console.error("Error:", error);
      // //console.log(error?.response?.data?.message || 'Error during payment submission!', 'error');
    }
  };
  
  // Check for network restoration and post data if available
  useEffect(() => {
    const postSavedData = async () => {
      const savedData = localStorage.getItem(`pending-payment-${id}`);
      if (savedData) {
        try {
          await axios.put(`/pays/${id}`, JSON.parse(savedData));
          // showAlert('Pago enviado exitosamente desde datos locales!', 'success');
          localStorage.removeItem(`pending-payment-${id}`);
          navigate(`/prestamos/abonos/pagos/invoice/${id}`);
          onButtonClick();
        } catch (error) {
          // showAlert(error?.response?.data?.message || 'Failed to submit offline payment data!', 'error');
        }
      }
    };
  
    // Debounce online handler to avoid multiple submissions on unstable connections
    let onlineHandlerTimeout: NodeJS.Timeout;
    const debouncedPost = () => {
      clearTimeout(onlineHandlerTimeout);
      onlineHandlerTimeout = setTimeout(postSavedData, 1000);
    };
  
    window.addEventListener('online', debouncedPost);
    
    return () => {
      clearTimeout(onlineHandlerTimeout);
      window.removeEventListener('online', debouncedPost);
    };
  }, [id]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={style}>
        <Title>Realizar Abono - ID: {id}</Title>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Valor Diario"
              value={daily_pay_value}
              onChange={(event) => setDaily_pay_value(event.target.value)}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Valor Abono"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Valor Descuento"
              value={value_discount}
              onChange={(event) => setValue_discount(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Valor Pendiente"
              value={pendiente || "0"}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              onClick={submit}
              sx={{
                mt: 2,
                mr: 2,
              }}
            >
              Guardar
            </Button>
            <Button
              variant="contained"
              onClick={onButtonClick}
              sx={{
                mt: 2,
                color: "white",
              }}
            >
              Cerrar
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default MyModal;
